// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field } from 'redux-form';
import { routerPush, getViewportInfo, isClient } from '@tcp/core/src/utils';
import validatorMethods from '@tcp/core/src/utils/formValidation/validatorMethods';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Button from '@tcp/core/src/components/common/atoms/Button';
import TextBox from '@tcp/core/src/components/common/atoms/TextBox';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import InputCheckbox from '@tcp/core/src/components/common/atoms/InputCheckbox';
import createValidateMethod from '@tcp/core/src/utils/formValidation/createValidateMethod';
import getStandardConfig from '@tcp/core/src/utils/formValidation/validatorStandardConfig';
import PasswordField from '@tcp/core/src/components/features/account/common/molecule/PasswordField/views/PasswordField.view';
import Recaptcha from '@tcp/core/src/components/common/molecules/recaptcha/recaptcha';
import SpinnerOverlay from '@tcp/core/src/components/common/atoms/SpinnerOverlay';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import { getContinueAsGuestPayload } from '@tcp/core/src/constants/analytics';
import ClickTracker from '@tcp/web/src/components/common/atoms/ClickTracker';
import LoginOptionView from '../../LoginOptions/views/LoginOptions.view';
import LineSeperator from '../../../atoms/LineSeperator';
import styles, { customSpinnerStyle, RememberForgotBlock } from '../styles/PasswordForm.styles';
import { getLabelFromCopyOrFromTranslation } from '../../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_LOGIN_LABELS,
  GlobalLoginLabels,
  lblLoginAndCheckout,
  lblLoginCheckoutAsGuest,
  lblLoginForgotPwdCta,
  lblLoginRememberMe,
  lblLoginRememberMeTxt,
  lblLoginSaveMyPlace,
  lblLoginTempPwd,
} from '../../../../../../../utils/labels/globalLogin.labels';
import {
  GLOBAL_ACCESSIBILITY_LABELS,
  GlobalAccessibilityLabels,
  lblPasswordTooltip,
} from '../../../../../../../utils/labels/globalAccessibility.labels';
import {
  GLOBAL_PASSWORD_LABELS,
  GlobalPasswordsLabels,
  lblPasswordSendCodeViaEmail,
} from '../../../../../../../utils/labels/globalPassword.labels';
import {
  GLOBAL_GENERIC_LABELS,
  GlobalGenericLabels,
  lblLogin,
} from '../../../../../../../utils/labels/globalGeneric.labels';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
} from '../../../../../../../utils/labels/globalActions.labels';

const OR = 'or';
const orRenderer = (isV2Flow) => {
  return (
    <BodyCopy
      className={`elem-mb-MED ${isV2Flow ? 'elem-mt-MED' : ''}`}
      component="p"
      textAlign="center"
      fontSize="fs14"
      fontWeight="semibold"
      fontFamily="secondary"
    >
      {OR}
    </BodyCopy>
  );
};
class PasswordForm extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isLoginClicked: false,
    };
    this.loginLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LOGIN_LABELS, GlobalLoginLabels);
  }

  componentDidUpdate(prevProps) {
    const { isLoading: prevIsLoading } = prevProps;
    if (prevIsLoading !== this.props.isLoading && this.props.isLoading === false) {
      this.setState({ isLoginClicked: false });
    }
  }

  showForgotPasswordForm = (e) => {
    e.preventDefault();

    const {
      showForgotPasswordForm,
      resetForm,
      resetLoginState,
      closeModal,
      closeOverlay,
      isResetPassCodeValidationEnabled,
      trackAnalyticsClick,
    } = this.props;
    trackAnalyticsClick({
      metric33: '1',
      eVar150: this.loginLabels[lblLoginForgotPwdCta],
      customEvents: ['event33'],
    });

    resetForm();
    resetLoginState();
    if (isResetPassCodeValidationEnabled) {
      closeModal();
      closeOverlay();
      routerPush(`/change-password?page=reset`, `/change-password?page=reset`);
    } else {
      showForgotPasswordForm();
    }
  };

  resetError = () => {
    const { resetLoginState, loginErrorMessage } = this.props;
    if (loginErrorMessage) {
      resetLoginState();
    }
  };

  updtateEmail = (event) => {
    const { setUserEmailAddress } = this.props;
    const emailValidator = validatorMethods.validEmail;
    setUserEmailAddress('');
    if (
      event &&
      event.target &&
      event.target.id === 'emailAddress' &&
      emailValidator(event.target.value)
    ) {
      setUserEmailAddress(event.target.value);
    }
  };

  loginLoader = () => {
    const { isLoading } = this.props;
    return <>{isLoading ? <SpinnerOverlay inheritedStyles={customSpinnerStyle} /> : null}</>;
  };

  focusFirstField = () => {
    const { fullPage } = this.props;
    if (isClient()) {
      const { isMobile = false } = getViewportInfo() || {};
      return fullPage && !isMobile;
    }
    return false;
  };

  sendCodeViaEmail = (e) => {
    const { onGenerateOTP } = this.props;
    e.preventDefault();
    onGenerateOTP({ otpChannel: 'email' });
  };

  forgetPasswordRenderer = () => {
    const { isNewLoginScreen } = this.props;
    return (
      <div className={!isNewLoginScreen ? 'elem-mb-LRCX' : ''}>
        <Anchor
          underline
          fontSizeVariation="large"
          anchorVariation="primary"
          dataLocator="login-forgotpasswordlnk"
          onClick={this.showForgotPasswordForm}
          id="forgotPasswordForm"
          className={isNewLoginScreen ? 'forgot-pass' : ''}
        >
          {this.loginLabels[lblLoginForgotPwdCta]}
        </Anchor>
      </div>
    );
  };

  rememberMeRenderer = () => {
    const { isNewLoginScreen } = this.props;
    return (
      <BodyCopy component="div" className={!isNewLoginScreen ? 'elem-mt-MED' : ''}>
        <Field
          name="rememberMe"
          component={InputCheckbox}
          dataLocator="login-remembermecb"
          className={`${isNewLoginScreen ? 'remember-me-new' : 'remember-me-checkbox '}`}
        >
          <BodyCopy
            fontFamily="secondary"
            tag="span"
            className="remember-me-text"
            fontWeight={isNewLoginScreen ? 'semibold' : 'normal'}
            fontSize={isNewLoginScreen ? 'fs12' : 'fs10'}
          >
            {this.loginLabels[lblLoginRememberMe]}
          </BodyCopy>
          <BodyCopy
            fontFamily="secondary"
            tag="span"
            fontSize="fs10"
            className={!isNewLoginScreen ? 'remember-me-sub-text' : ''}
          >
            {this.loginLabels[lblLoginRememberMeTxt]}
          </BodyCopy>
        </Field>
      </BodyCopy>
    );
  };

  loginBtnRenderer = () => {
    const { isLoading, pristine, isV2Flow, isNewLoginScreen, fromPlccICLogin } = this.props;
    const { isLoginClicked } = this.state;
    const shouldLoginButtonDisabled = () => pristine || isLoading;
    if (fromPlccICLogin) return null;
    return (
      <Button
        fill="BLUE"
        type="submit"
        className="elem-mb-MED rounded-corner"
        buttonVariation="fixed-width"
        dataLocator="login-logincta"
        fullWidth
        disabled={shouldLoginButtonDisabled()}
        loading={
          (isLoading && !isNewLoginScreen) || (isNewLoginScreen && isLoading && isLoginClicked)
        }
        spc
        onClick={() => this.setState({ isLoginClicked: true })}
      >
        {!isV2Flow
          ? getLabelFromCopyOrFromTranslation(
              `${GLOBAL_ACTIONS_LABELS}.${lblLogin}`,
              GlobalActionsLabels
            )
          : this.loginLabels[lblLoginAndCheckout]}
      </Button>
    );
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  render() {
    const {
      handleSubmit,
      loginErrorMessage,
      className,
      showRecaptcha,
      change,
      handleContinueAsGuest,
      tooltipContent,
      userplccCardNumber,
      userplccCardId,
      isRememberedUser,
      isV2Flow,
      isLoginWithEmailOTPEnabled,
      emailAddress,
      isLoading,
      isNewLoginScreen,
      isLoginModal,
      rememberedEmail,
      fromPlccICLogin,
      pristine,
    } = this.props;
    const { isLoginClicked } = this.state;

    const { lbl_emailAddress: lblEmailAddress, lbl_password: lblPassword } =
      getLabelFromCopyOrFromTranslation(GLOBAL_GENERIC_LABELS, GlobalGenericLabels);
    const shouldLoginButtonDisabled = () => pristine || isLoading;

    return (
      <div className={className}>
        {isNewLoginScreen && isLoading && !isLoginClicked && (
          <SpinnerOverlay isLoginModal={isLoginModal} isNewLoginScreen={isNewLoginScreen} />
        )}
        <form name="LoginForm" onSubmit={handleSubmit} noValidate className={className}>
          {loginErrorMessage && (
            <BodyCopy fontSize="fs12" fontWeight="semibold" color="red.500" className="elem-mb-XL">
              <RichText className="richTextColor" richTextHtml={loginErrorMessage} />
            </BodyCopy>
          )}
          <BodyCopy component="div" className="elem-mb-LRG">
            {isRememberedUser && !isNewLoginScreen && !isV2Flow && (
              <Field
                id="emailAddress"
                placeholder={lblEmailAddress}
                name="emailAddress"
                component={TextBox}
                dataLocator="login-emailfield"
                className="elem-mb-SM"
                props={{
                  readOnly: true,
                }}
                autoFocus={this.focusFirstField()}
              />
            )}
            <Field
              id="password"
              placeholder={`${lblPassword}`}
              name="password"
              component={PasswordField}
              dataLocator="login-passwordfield"
              errorDataLocator="login-passworderror"
              showSuccessCheck={false}
              enableSuccessCheck={false}
              className={`elem-mb-SM ${
                isNewLoginScreen ? 'newLoginScreenPassField elem-mt-SM' : ''
              }`}
              tooltipContent={tooltipContent}
              onChange={this.resetError}
              tooltipAriaLabel={getLabelFromCopyOrFromTranslation(
                `${GLOBAL_ACCESSIBILITY_LABELS}.${lblPasswordTooltip}`,
                GlobalAccessibilityLabels
              )}
              spc
              newLoginScreen={isNewLoginScreen}
            />

            {userplccCardNumber && userplccCardId && (
              <BodyCopy component="div" className="save-my-plcc">
                <Field
                  name="plcc_checkbox"
                  component={InputCheckbox}
                  dataLocator="login-savemyplcccardcb"
                >
                  <BodyCopy fontFamily="secondary" tag="span" fontSize="fs10">
                    {this.loginLabels[lblLoginSaveMyPlace]?.replace(
                      '#number',
                      `${userplccCardNumber}`
                    )}
                  </BodyCopy>
                </Field>
              </BodyCopy>
            )}

            <BodyCopy component="div">
              {showRecaptcha && (
                <>
                  <Recaptcha
                    verifyCallback={(token) => change('recaptchaToken', token)}
                    expiredCallback={() => change('recaptchaToken', '')}
                  />
                  <Field
                    component={TextBox}
                    type="hidden"
                    name="recaptchaToken"
                    data-locator="login-recaptchcb"
                  />
                </>
              )}
            </BodyCopy>
            {!isNewLoginScreen && this.rememberMeRenderer()}
            {isNewLoginScreen && (
              <>
                <RememberForgotBlock>
                  {this.rememberMeRenderer()}
                  {!fromPlccICLogin && this.forgetPasswordRenderer()}
                </RememberForgotBlock>
                <div className="elem-mt-LRG">{this.loginBtnRenderer()}</div>
                {!isV2Flow && orRenderer(isV2Flow)}
                <LineSeperator />
                {isLoginWithEmailOTPEnabled && (
                  <LoginOptionView
                    type="email"
                    emailAddress={isRememberedUser ? rememberedEmail : emailAddress}
                    submitForm={this.sendCodeViaEmail}
                  />
                )}
                {isV2Flow && !isRememberedUser && orRenderer(isV2Flow)}
              </>
            )}
          </BodyCopy>
          {isLoginWithEmailOTPEnabled && !isNewLoginScreen && (
            <BodyCopy component="div" className="elem-mt-LRG elem-mb-LRG">
              <BodyCopy
                textAlign="center"
                fontFamily="secondary"
                fontWeight="semibold"
                fontSize="fs14"
                dataLocator="login-resend-text"
              >
                {this.loginLabels[lblLoginTempPwd]}
              </BodyCopy>
              <BodyCopy component="div" textAlign="center">
                <Anchor
                  underline
                  anchorVariation="primary"
                  dataLocator="login-resend-email-link"
                  className="resend-email-link"
                  onClick={this.sendCodeViaEmail}
                >
                  {getLabelFromCopyOrFromTranslation(
                    `${GLOBAL_PASSWORD_LABELS}.${lblPasswordSendCodeViaEmail}`,
                    GlobalPasswordsLabels
                  )}
                </Anchor>
              </BodyCopy>
            </BodyCopy>
          )}
          <BodyCopy component="div" textAlign="center" className="elem-mb-LRCX">
            {!isNewLoginScreen && this.loginBtnRenderer()}
            {!isRememberedUser && (
              <>
                {!isNewLoginScreen && this.forgetPasswordRenderer()}
                {isV2Flow && (
                  <div className="elem-mb-LRG">
                    <ClickTracker clickData={getContinueAsGuestPayload('checkout-popup')}>
                      <Button
                        fill="WHITE"
                        type="button"
                        buttonVariation="fixed-width"
                        dataLocator="login-logincta"
                        fullWidth
                        onClick={handleContinueAsGuest}
                        className="rounded-corner test-guest-button"
                        spc
                      >
                        {this.loginLabels[lblLoginCheckoutAsGuest]}
                      </Button>
                    </ClickTracker>
                  </div>
                )}
              </>
            )}
          </BodyCopy>
          {fromPlccICLogin && (
            <div className="plcc-footer-cta-section">
              <BodyCopy className="plcc-login-cta-container" component="div" textAlign="left">
                <Button
                  fill="BLUE"
                  type="submit"
                  className="elem-mb-LRG elem-mt-LRG plcc-border-style"
                  buttonVariation="fixed-width"
                  dataLocator="login-logincta"
                  fullWidth
                  disabled={shouldLoginButtonDisabled()}
                  loading={
                    (isLoading && !isNewLoginScreen) ||
                    (isNewLoginScreen && isLoading && isLoginClicked)
                  }
                  spc
                  onClick={() => this.setState({ isLoginClicked: true })}
                >
                  {'LOGIN AND SUBMIT ORDER'}
                </Button>
              </BodyCopy>
            </div>
          )}
        </form>
        {this.loginLoader}
      </div>
    );
  }
}

PasswordForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  className: PropTypes.string,
  loginErrorMessage: PropTypes.string,
  showRecaptcha: PropTypes.bool,
  change: PropTypes.func,
  pristine: PropTypes.bool,
  handleContinueAsGuest: PropTypes.func.isRequired,
  userplccCardNumber: PropTypes.string.isRequired,
  userplccCardId: PropTypes.string.isRequired,
  showForgotPasswordForm: PropTypes.bool.isRequired,
  resetForm: PropTypes.bool.isRequired,
  resetLoginState: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  tooltipContent: PropTypes.string.isRequired,
  isRememberedUser: PropTypes.bool.isRequired,
  closeModal: PropTypes.func,
  closeOverlay: PropTypes.func,
  isResetPassCodeValidationEnabled: PropTypes.bool,
  fullPage: PropTypes.bool,
};

PasswordForm.defaultProps = {
  className: '',
  loginErrorMessage: '',
  showRecaptcha: false,
  change: () => {},
  pristine: false,
  closeModal: () => {},
  closeOverlay: () => {},
  isResetPassCodeValidationEnabled: false,
  fullPage: false,
};

const validateMethod = createValidateMethod(
  getStandardConfig([
    { emailAddress: 'emailAddressNoAsync' },
    { password: 'legacyPassword' },
    'recaptchaToken',
  ])
);

export default reduxForm({
  form: 'PasswordForm', // a unique identifier for this form
  enableReinitialize: true,
  ...validateMethod,
})(withStyles(PasswordForm, styles));

export { PasswordForm as PasswordFormVanilla };
